import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PartnerLogos from "@/components/shared/PartnerLogos";
import PageHero from "@/components/shared/PageHero";
import VibrantSectionHeader from "@/components/shared/VibrantSectionHeader";
import CTASection from "@/components/shared/CTASection";
import PageSEO from "@/components/shared/PageSEO";
import Breadcrumbs from "@/components/shared/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { 
  Building2, 
  Landmark, 
  Car, 
  Wrench,
  Paintbrush,
  Zap,
  Building,
  Leaf,
  Shield,
  Clock,
  CheckCircle2,
  ArrowRight
} from "lucide-react";
import { useState } from "react";

const services = [
  {
    icon: Building2,
    title: "B2B Solutions",
    subtitle: "Corporate Mobility Partner",
    slug: "b2b-solutions",
    description: "Enterprise-level mobility solutions for businesses looking to modernize their fleet operations and reduce environmental impact.",
    features: ["Fleet Management", "Custom Branding", "Bulk Pricing", "Maintenance Support"],
    category: "B2B",
    color: "blue" as const,
  },
  {
    icon: Landmark,
    title: "B2G Services",
    subtitle: "Government & Public Sector",
    slug: "b2g-services",
    description: "Specialized mobility solutions for government institutions, municipalities, and public sector organizations.",
    features: ["Tender Support", "Compliance Ready", "Large Scale Deployment", "Training Programs"],
    category: "B2G",
    color: "gold" as const,
  },
  {
    icon: Car,
    title: "Leasing Programs",
    subtitle: "On-Demand Fleet Leasing",
    slug: "leasing-mobility",
    description: "Flexible leasing options for events, seasonal operations, and temporary deployments without capital investment.",
    features: ["Short & Long Term", "Maintenance Included", "Rapid Deployment", "Brand Customization"],
    category: "Leasing",
    color: "green" as const,
  },
  {
    icon: Wrench,
    title: "Service & Maintenance",
    subtitle: "Authorized Service Provider",
    slug: "service-maintenance",
    description: "Comprehensive after-sales support, repairs, spare parts, and technical maintenance from certified technicians.",
    features: ["Preventive Maintenance", "24/7 Emergency Support", "Genuine Spare Parts", "On-site Service"],
    category: "Operations",
    color: "teal" as const,
  },
  {
    icon: Paintbrush,
    title: "Fleet Customization",
    subtitle: "Modification & Branding",
    slug: "fleet-customization",
    description: "Transform vehicles into purpose-built solutions with custom branding, livery, and equipment integration.",
    features: ["Custom Branding", "Emergency Equipment", "Medical Installation", "Security Integration"],
    category: "Operations",
    color: "purple" as const,
  },
  {
    icon: Zap,
    title: "Motorized Mobility",
    subtitle: "Electric & Combustion",
    slug: "motorized-mobility",
    description: "Complete range of motorized mobility spanning electric and combustion-powered vehicles for diverse needs.",
    features: ["Electric Fleet", "Combustion Options", "Hybrid Solutions", "Industrial Vehicles"],
    category: "Operations",
    color: "blue" as const,
  },
  {
    icon: Building,
    title: "Mega Projects",
    subtitle: "Run & Operate",
    slug: "mega-projects",
    description: "Complete operational management for large-scale mobility deployments at mega events and smart cities.",
    features: ["Project Design", "Fleet Deployment", "Staff Management", "Operations Center"],
    category: "Operations",
    color: "gold" as const,
  },
];

const advantages = [
  {
    icon: Leaf,
    title: "100% Sustainable",
    description: "Zero emissions, fully electric fleet aligned with Vision 2030 environmental goals.",
    color: "green" as const,
  },
  {
    icon: Zap,
    title: "Cost Efficient",
    description: "Reduce operational costs by up to 60% compared to traditional fuel-powered vehicles.",
    color: "blue" as const,
  },
  {
    icon: Shield,
    title: "Reliable & Safe",
    description: "Premium quality vehicles with comprehensive safety features and certifications.",
    color: "gold" as const,
  },
  {
    icon: Clock,
    title: "Quick Deployment",
    description: "From consultation to full deployment in as little as 2 weeks.",
    color: "teal" as const,
  },
];

const testimonials = [
  {
    quote: "SDC's electric ambulance support vehicles have transformed our emergency response capabilities.",
    author: "Dubai Ambulance Services",
    role: "Emergency Medical Services",
  },
  {
    quote: "The N13 MAX fleet has been instrumental in our track operations during the Formula 1 events.",
    author: "Jeddah Corniche Circuit",
    role: "Motorsport Operations",
  },
  {
    quote: "Sustainable Development Company helped us achieve our green initiatives ahead of schedule.",
    author: "Exhibition Center",
    role: "Facility Management",
  },
];

const categories = ["All", "B2B", "B2G", "Leasing", "Operations"];

const colorMap: Record<string, string> = {
  blue: "from-sky-blue/20 to-sky-blue/5 border-sky-blue/20 hover:border-sky-blue/40",
  green: "from-eco-green/20 to-eco-green/5 border-eco-green/20 hover:border-eco-green/40",
  purple: "from-purple/20 to-purple/5 border-purple/20 hover:border-purple/40",
  teal: "from-teal-bright/20 to-teal-bright/5 border-teal-bright/20 hover:border-teal-bright/40",
  gold: "from-primary/20 to-amber-glow/5 border-primary/20 hover:border-primary/40",
};

const iconBgMap: Record<string, string> = {
  blue: "bg-gradient-to-br from-sky-blue to-sky-blue-light",
  green: "bg-gradient-to-br from-eco-green to-eco-green-light",
  purple: "bg-gradient-to-br from-purple to-rose",
  teal: "bg-gradient-to-br from-teal-bright to-eco-green",
  gold: "bg-gradient-to-br from-primary to-amber-glow",
};

const Services = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const filteredServices = activeCategory === "All" 
    ? services 
    : services.filter(service => service.category === activeCategory);

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="B2B & B2G Mobility Services Saudi Arabia"
        description="Comprehensive B2B and B2G micro mobility services in Saudi Arabia. Fleet management, leasing, maintenance & customization. خدمات التنقل الصغير للشركات والجهات الحكومية في السعودية. تأجير مركبات كهربائية."
        keywords="B2B mobility Saudi Arabia, B2G services KSA, fleet management Saudi, تأجير مركبات كهربائية, electric vehicle leasing KSA, maintenance services Saudi Arabia, خدمات صيانة مركبات كهربائية, fleet customization, micro mobility services, حلول التنقل للشركات"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What B2B mobility services does SDC offer in Saudi Arabia?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "SDC provides comprehensive B2B mobility services including fleet supply, leasing programs, maintenance, fleet customization, and mega project operations management across Saudi Arabia."
              }
            },
            {
              "@type": "Question",
              "name": "Does SDC provide electric vehicle leasing in KSA? هل توفر SDC تأجير مركبات كهربائية؟",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, SDC offers flexible electric vehicle leasing programs for events, seasonal operations, and long-term deployments. Our leasing includes maintenance, rapid deployment, and brand customization options."
              }
            }
          ]
        }}
      />
      <Navbar />
      
      <PageHero
        title="Our Services"
        subtitle="Your Trusted Mobility Partner"
        description="Comprehensive B2B and B2G mobility solutions tailored to transform your operations and drive sustainable growth."
        variant="vibrant"
      />

      <Breadcrumbs />

      {/* Category Pills */}
      <section className="py-8 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === category
                    ? "bg-primary text-primary-foreground"
                    : "bg-card border border-border text-muted-foreground hover:border-primary/50"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-eco-green/5 via-transparent to-sky-blue/5" />
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="What We Offer"
            title="Our Service Pillars"
            description="Seven specialized pillars designed to transform your mobility operations"
            badgeColor="green"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service, index) => (
              <div
                key={index}
                className={`group p-6 rounded-2xl bg-gradient-to-br ${colorMap[service.color]} border transition-all duration-500 hover:-translate-y-2 hover:shadow-xl relative overflow-hidden ${
                  filteredServices.length % 3 === 1 && index === filteredServices.length - 1 ? 'lg:col-start-2' : ''
                }`}
              >
                {/* Glow effect */}
                <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full ${iconBgMap[service.color]} blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />
                
                <div className="relative z-10">
                  <div className={`w-14 h-14 rounded-xl ${iconBgMap[service.color]} flex items-center justify-center mb-4 shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-xs text-primary font-medium uppercase tracking-wider">
                    {service.subtitle}
                  </span>
                  <h3 className="text-xl font-bold text-foreground mt-2 mb-3">{service.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-eco-green" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button variant="ghost" className="group-hover:text-primary p-0" asChild>
                    <Link to={`/services/${service.slug}`}>
                      Explore Service <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-secondary/50 to-secondary/30" />
        <div className="absolute -left-40 top-1/2 w-80 h-80 rounded-full bg-eco-green/10 blur-3xl" />
        <div className="absolute -right-40 top-1/2 w-80 h-80 rounded-full bg-sky-blue/10 blur-3xl" />
        
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Why Choose Us"
            title="Why Partner with SDC"
            description="Experience the advantages of working with a sustainability-focused mobility partner"
            badgeColor="gold"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((advantage, index) => (
              <div
                key={index}
                className={`group text-center p-6 rounded-2xl bg-gradient-to-br ${colorMap[advantage.color]} border transition-all duration-500 hover:-translate-y-2 hover:shadow-xl relative overflow-hidden`}
              >
                <div className={`absolute -top-10 -right-10 w-24 h-24 rounded-full ${iconBgMap[advantage.color]} blur-3xl opacity-0 group-hover:opacity-20 transition-opacity`} />
                
                <div className={`w-16 h-16 rounded-full ${iconBgMap[advantage.color]} flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                  <advantage.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{advantage.title}</h3>
                <p className="text-sm text-muted-foreground">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-eco-green/5" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-3xl" />
        
        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Client Success"
            title="What Our Clients Say"
            badgeColor="blue"
          />
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-sky-blue/10 to-primary/5 border border-sky-blue/20 rounded-2xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-sky-blue/20 blur-3xl" />
              <p className="text-xl md:text-2xl text-foreground italic mb-8 relative z-10">
                "{testimonials[activeTestimonial].quote}"
              </p>
              <div className="relative z-10">
                <p className="font-bold text-foreground">{testimonials[activeTestimonial].author}</p>
                <p className="text-sm text-muted-foreground">{testimonials[activeTestimonial].role}</p>
              </div>
            </div>
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    activeTestimonial === index 
                      ? "bg-gradient-to-r from-primary to-eco-green scale-125" 
                      : "bg-border hover:bg-primary/30"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <PartnerLogos />

      <CTASection
        title="Ready to Transform Your Operations?"
        description="Get in touch with our team to discuss how we can help modernize your mobility infrastructure."
        primaryButtonText="Contact Us"
        primaryButtonLink="/contact"
        secondaryButtonText="View Projects"
        secondaryButtonLink="/projects"
      />

      <Footer />
    </div>
  );
};

export default Services;
